import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { formatMoney } from '@/utils/format';
import { Est3VirtualList } from './Est3VirtualList';
import { EpcEstimateCostLineDrawer } from './EpcEstimateCostLineDrawer';
import { EpcEstimateBulkAssignBar } from './EpcEstimateBulkAssignBar';
import { EpcEstimateCostLineAddDrawer } from './EpcEstimateCostLineAddDrawer';
import { getEst3CostProvenance } from './est3CostProvenance';

const { Text } = Typography;

const RATE_SOURCES = [
  'MANUAL',
  'CATALOGUE',
  'LAST_PURCHASE',
  'SUPPLIER_QUOTE',
  'INDEXED',
  'HISTORICAL',
] as const;

const VIRTUAL_THRESHOLD = 50;
const ROW_HEIGHT = 40;

export interface EpcEstimateDirectCostWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  lines: any[];
  wbsOptions: Array<{ id: string; code: string; name?: string }>;
  cbsOptions: Array<{ id: string; code: string; name?: string }>;
  boqOptions: Array<{ id: string; item_number?: string; description?: string }>;
  filterBoqLineId?: string | null;
  loading?: boolean;
  costForm: FormInstance;
  onAdd: (values: Record<string, unknown>) => void;
  addPending?: boolean;
  /** When set, show only lines of this resource kind (Materials / Labour / Equipment leaves). */
  kindFilter?: 'MATERIAL' | 'LABOUR' | 'EQUIPMENT';
  showAddForm?: boolean;
  title?: string;
  onOpenMaterials?: (lineId: string) => void;
  onOpenLabour?: (lineId: string) => void;
  onOpenEquipment?: (lineId: string) => void;
  onBulkAssign?: (
    lineIds: string[],
    patch: { wbsNodeId?: string; cbsCodeId?: string; clientBoqLineId?: string }
  ) => Promise<void>;
  bulkAssignPending?: boolean;
  useDrawerAdd?: boolean;
  defaultBoqLineId?: string | null;
  onSetupWbs?: () => void;
}

function labelForId(
  id: string | null | undefined,
  options: Array<{ id: string; code?: string; item_number?: string; description?: string; name?: string }>
): string | undefined {
  if (!id) return undefined;
  const hit = options.find((o) => o.id === id);
  if (!hit) return id.slice(0, 8);
  return hit.code || hit.item_number || hit.description || hit.name;
}

/**
 * EST3 UX2 Wave 3 — Direct cost workbench (virtual grid, selection, provenance drawer).
 */
export const EpcEstimateDirectCostWorkbench: React.FC<EpcEstimateDirectCostWorkbenchProps> = ({
  readOnly,
  lines,
  wbsOptions,
  cbsOptions,
  boqOptions,
  filterBoqLineId,
  loading,
  costForm,
  onAdd,
  addPending,
  kindFilter,
  showAddForm = true,
  title,
  onOpenMaterials,
  onOpenLabour,
  onOpenEquipment,
  onBulkAssign,
  bulkAssignPending,
  useDrawerAdd = true,
  defaultBoqLineId,
  onSetupWbs,
}) => {
  const [filter, setFilter] = useState('');
  const [kind, setKind] = useState<string | undefined>(kindFilter);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [detailLine, setDetailLine] = useState<Record<string, unknown> | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (lines || []).filter((row) => {
      if (filterBoqLineId === '__unlinked__' && row.client_boq_line_id) return false;
      if (filterBoqLineId && filterBoqLineId !== '__unlinked__' && row.client_boq_line_id !== filterBoqLineId) return false;
      if (kindFilter && row.resource_kind !== kindFilter) return false;
      if (kind && !kindFilter && row.resource_kind !== kind) return false;
      if (!q) return true;
      const prov = getEst3CostProvenance(row).summary.toLowerCase();
      const hay = `${row.description || ''} ${row.resource_kind || ''} ${prov}`.toLowerCase();
      return hay.includes(q);
    });
  }, [lines, filter, kind, kindFilter, filterBoqLineId]);

  const boqMap = useMemo(
    () =>
      new Map(
        boqOptions.map((b) => [b.id, b.item_number || b.description || b.id.slice(0, 6)])
      ),
    [boqOptions]
  );
  const wbsMap = useMemo(() => new Map(wbsOptions.map((w) => [w.id, w.code])), [wbsOptions]);
  const cbsMap = useMemo(() => new Map(cbsOptions.map((c) => [c.id, c.code])), [cbsOptions]);

  const renderActions = (row: any) => {
    const id = row.id as string;
    const rk = row.resource_kind;
    return (
      <Space size={4}>
        <Button size="small" type="link" onClick={() => setDetailLine(row)}>
          Detail
        </Button>
        {rk === 'MATERIAL' && onOpenMaterials ? (
          <Button size="small" onClick={() => onOpenMaterials(id)}>
            Mat
          </Button>
        ) : null}
        {rk === 'LABOUR' && onOpenLabour ? (
          <Button size="small" onClick={() => onOpenLabour(id)}>
            Crew
          </Button>
        ) : null}
        {rk === 'EQUIPMENT' && onOpenEquipment ? (
          <Button size="small" onClick={() => onOpenEquipment(id)}>
            Equip
          </Button>
        ) : null}
      </Space>
    );
  };

  const virtualRow = (row: any) => {
    const prov = getEst3CostProvenance(row);
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 8px',
          height: ROW_HEIGHT,
          fontSize: 12,
          cursor: 'pointer',
          background: selectedKeys.includes(row.id) ? '#e6f4ff' : undefined,
        }}
        onClick={() => setDetailLine(row)}
      >
        <span style={{ width: 28, flexShrink: 0 }}>
          <input
            type="checkbox"
            checked={selectedKeys.includes(row.id)}
            onChange={(e) => {
              e.stopPropagation();
              setSelectedKeys((prev) =>
                e.target.checked ? [...prev, row.id] : prev.filter((k) => k !== row.id)
              );
            }}
          />
        </span>
        <span style={{ flex: 2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.description}
        </span>
        <Tag style={{ margin: 0 }}>{row.resource_kind}</Tag>
        <span style={{ width: 56, textAlign: 'right' }}>{row.quantity}</span>
        <span style={{ width: 88, textAlign: 'right' }}>
          {formatMoney(row.total_amount, { display: 'plain', maximumFractionDigits: 0 })}
        </span>
        <Tag color="blue" style={{ margin: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {prov.summary !== '—' ? prov.summary : row.rate_source}
        </Tag>
        <span style={{ width: 100, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          {renderActions(row)}
        </span>
      </div>
    );
  };

  const tableColumns = [
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Kind', dataIndex: 'resource_kind', width: 100 },
    {
      title: 'WBS',
      width: 80,
      render: (_: unknown, r: any) => wbsMap.get(r.wbs_node_id) || '—',
    },
    {
      title: 'CBS',
      width: 80,
      render: (_: unknown, r: any) => cbsMap.get(r.cbs_code_id) || '—',
    },
    {
      title: 'BOQ',
      width: 80,
      render: (_: unknown, r: any) => {
        const id = r.client_boq_line_id;
        if (!id) return <Tag color="orange">—</Tag>;
        return boqMap.get(id) || id.slice(0, 6);
      },
    },
    { title: 'Qty', dataIndex: 'quantity', width: 70 },
    {
      title: 'Rate',
      dataIndex: 'rate',
      width: 90,
      render: (v: unknown) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 100,
      render: (v: unknown) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
    },
    {
      title: 'Provenance',
      width: 160,
      ellipsis: true,
      render: (_: unknown, row: any) => {
        const p = getEst3CostProvenance(row);
        return p.summary !== '—' ? <Tag color="blue">{p.summary}</Tag> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: '',
      width: 140,
      render: (_: unknown, row: any) => renderActions(row),
    },
  ];

  return (
    <Card size="small" title={title}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Filter description / kind / provenance"
              style={{ width: 260 }}
              onSearch={setFilter}
              onChange={(e) => {
                if (!e.target.value) setFilter('');
              }}
            />
            {!kindFilter ? (
              <Select
                allowClear
                placeholder="Kind"
                style={{ width: 130 }}
                value={kind}
                onChange={setKind}
                options={['MATERIAL', 'LABOUR', 'EQUIPMENT', 'SUBCONTRACT', 'OTHER'].map((v) => ({
                  value: v,
                  label: v,
                }))}
              />
            ) : null}
            <Text type="secondary">
              {filtered.length} line(s)
              {selectedKeys.length ? ` · ${selectedKeys.length} selected` : ''}
            </Text>
          </Space>
          {!readOnly && showAddForm && useDrawerAdd ? (
            <Button type="primary" onClick={() => setAddOpen(true)}>
              Add cost line
            </Button>
          ) : null}
        </Space>

        {readOnly ? (
          <Alert type="info" showIcon message="Approved revision — costs are read-only" />
        ) : showAddForm && !useDrawerAdd ? (
          <Form
            form={costForm}
            layout="inline"
            onFinish={(v) =>
              onAdd({
                ...v,
                resourceKind: kindFilter || v.resourceKind || 'MATERIAL',
                rateSource: v.rateSource || 'MANUAL',
              })
            }
          >
            <Form.Item name="description" rules={[{ required: true }]}>
              <Input placeholder="Description" style={{ width: 180 }} />
            </Form.Item>
            <Form.Item name="wbsNodeId" rules={[{ required: true }]}>
              <Select
                placeholder="WBS"
                style={{ width: 120 }}
                options={wbsOptions.map((n) => ({ value: n.id, label: n.code }))}
              />
            </Form.Item>
            <Form.Item name="cbsCodeId" rules={[{ required: true }]}>
              <Select
                placeholder="CBS"
                style={{ width: 120 }}
                options={cbsOptions.map((n) => ({ value: n.id, label: n.code }))}
              />
            </Form.Item>
            <Form.Item name="clientBoqLineId">
              <Select
                allowClear
                placeholder="Client BOQ"
                style={{ width: 140 }}
                options={boqOptions.map((n) => ({
                  value: n.id,
                  label: n.item_number || n.description,
                }))}
              />
            </Form.Item>
            <Form.Item name="quantity" initialValue={1}>
              <InputNumber min={0} placeholder="Qty" />
            </Form.Item>
            <Form.Item name="rate" initialValue={0}>
              <InputNumber min={0} placeholder="Rate" />
            </Form.Item>
            {!kindFilter ? (
              <Form.Item name="resourceKind" initialValue="MATERIAL">
                <Select
                  style={{ width: 110 }}
                  options={['MATERIAL', 'LABOUR', 'EQUIPMENT'].map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            ) : null}
            <Form.Item name="rateSource" initialValue="MANUAL">
              <Select
                style={{ width: 130 }}
                options={RATE_SOURCES.map((v) => ({ value: v, label: v }))}
              />
            </Form.Item>
            <Form.Item name="sourceRefLabel">
              <Input placeholder="Provenance label" style={{ width: 140 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={addPending}>
              Add
            </Button>
          </Form>
        ) : null}

        {filtered.length > VIRTUAL_THRESHOLD ? (
          <>
            <div
              style={{
                display: 'flex',
                gap: 8,
                padding: '0 8px',
                fontWeight: 600,
                fontSize: 12,
                color: '#595959',
              }}
            >
              <span style={{ width: 28 }} />
              <span style={{ flex: 2 }}>Description</span>
              <span style={{ width: 72 }}>Kind</span>
              <span style={{ width: 56, textAlign: 'right' }}>Qty</span>
              <span style={{ width: 88, textAlign: 'right' }}>Total</span>
              <span style={{ width: 120 }}>Provenance</span>
              <span style={{ width: 100 }} />
            </div>
            <Est3VirtualList items={filtered} rowHeight={ROW_HEIGHT} renderRow={virtualRow} />
          </>
        ) : (
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filtered}
            pagination={false}
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: setSelectedKeys,
            }}
            onRow={(row) => ({
              onClick: () => setDetailLine(row),
              style: { cursor: 'pointer' },
            })}
            columns={tableColumns}
          />
        )}
      </Space>

      <EpcEstimateCostLineDrawer
        line={detailLine}
        open={!!detailLine}
        onClose={() => setDetailLine(null)}
        readOnly={readOnly}
        wbsOptions={wbsOptions}
        cbsOptions={cbsOptions}
        wbsLabel={labelForId(String(detailLine?.wbs_node_id || ''), wbsOptions)}
        cbsLabel={labelForId(String(detailLine?.cbs_code_id || ''), cbsOptions)}
        boqLabel={labelForId(String(detailLine?.client_boq_line_id || ''), boqOptions)}
        boqOptions={boqOptions}
        onOpenMaterials={onOpenMaterials}
        onOpenLabour={onOpenLabour}
        onOpenEquipment={onOpenEquipment}
      />

      <EpcEstimateCostLineAddDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        form={costForm}
        readOnly={readOnly}
        wbsOptions={wbsOptions}
        cbsOptions={cbsOptions}
        boqOptions={boqOptions}
        costLines={lines}
        kindFilter={kindFilter}
        onSetupWbs={onSetupWbs}
        onSubmit={onAdd}
        pending={addPending}
        defaultBoqLineId={defaultBoqLineId}
      />

      <EpcEstimateBulkAssignBar
        selectedIds={selectedKeys.map(String)}
        totalCount={filtered.length}
        readOnly={readOnly || !onBulkAssign}
        wbsOptions={wbsOptions}
        cbsOptions={cbsOptions}
        boqOptions={boqOptions}
        pending={bulkAssignPending}
        onClearSelection={() => setSelectedKeys([])}
        onSelectAll={() => setSelectedKeys(filtered.map((r) => r.id))}
        onAssign={async (patch) => {
          if (!onBulkAssign) return;
          await onBulkAssign(selectedKeys.map(String), patch);
          setSelectedKeys([]);
        }}
      />
    </Card>
  );
};
